/**
 * @file UpdateDesignDialog.tsx
 * @description Окно «Обновить дизайн «…» из текущего вида?» (phase19.16;
 * решения пользователя 2026-10-12): содержимое дизайна заменяется черновиком,
 * прежний вид дизайна не сохраняется. Галочка «Вместе с логотипами»
 * (начальное — как в дизайне). Если дизайн сейчас на сайте и черновик
 * отличается — предупреждение и страховочная копия вида с сайта (включена;
 * не нужна, если этот вид уже есть среди других дизайнов). Отказ сервера
 * (назначен страницам и др.) — текстом в окне.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { CheckRow } from "../../site-view/CheckRow";
import type { UpdateDesignInput } from "../../../hooks/site-builder/useDesigns";
import type { SiteDesignItem, SiteDesignList } from "../../../lib/api-site-designs";
import { designErrorText } from "../../../lib/site-view/design-errors";
import { publishedCopyBaseName, uniqueDesignName } from "../../../lib/site-view/design-status";
import { SafetyCopyRow, STICKY_FOOTER } from "./SafetyCopyRow";

type Phase = "idle" | "settle" | "update";

interface Props {
  /** Какой дизайн обновить; `null` — окно закрыто. */
  item: SiteDesignItem | null;
  list: SiteDesignList;
  onClose: () => void;
  /** Обновить; ошибку окно покажет само. */
  onSubmit: (item: SiteDesignItem, input: UpdateDesignInput, onSettled: () => void) => Promise<void>;
}

export function UpdateDesignDialog({ item, list, onClose, onSubmit }: Props) {
  const [withLogos, setWithLogos] = useState(false);
  const [copySite, setCopySite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const itemId = item?.id ?? null;

  useEffect(() => {
    if (!item) return;
    // Совпадает с черновиком, но без логотипов — окно открыли ради логотипов.
    setWithLogos(item.summary.withLogos || item.matchesDraft);
    setCopySite(true);
    setError(null);
    setPhase("idle");
    // Сброс только при открытии другого дизайна, не при перечитывании списка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  if (!item) return null;

  // Дизайн на сайте, а черновик другой: после обновления вид сайта не будет сохранён ни в одном дизайне.
  const onSite = item.matchesPublished && !item.matchesDraft;
  const showCopy = onSite && !list.items.some((d) => d.id !== item.id && d.matchesPublished);
  const copyFull = list.limit.used + 1 > list.limit.max;
  const copyName = uniqueDesignName(list.items, publishedCopyBaseName(item.name));

  async function submit(target: SiteDesignItem) {
    setError(null);
    setPhase("settle");
    try {
      await onSubmit(
        target,
        { withLogos, publishedCopyName: showCopy && copySite && !copyFull ? copyName : null },
        () => setPhase("update"),
      );
      onClose();
    } catch (err) {
      setError(designErrorText(err, "Не удалось обновить дизайн."));
    } finally {
      setPhase("idle");
    }
  }

  return (
    <Dialog open onOpenChange={(next) => (!next && phase === "idle" ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[35rem] sm:max-h-[calc(100dvh-2rem)] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Обновить дизайн «{item.name}» из текущего вида?</DialogTitle>
          <DialogDescription>
            Цвета, шрифты, формы, фон, шапка и подвал дизайна заменятся тем, что сейчас в черновике. Прежний вид этого дизайна не сохранится.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
          <CheckRow checked={withLogos} onChange={setWithLogos}>
            Вместе с логотипами
          </CheckRow>
          {onSite ? (
            <NoticeStrip tone="warning">
              Этот дизайн сейчас на сайте. Покупатели ничего не заметят до «Опубликовать». Но метка «На сайте» с него снимется, а нынешний
              вид сайта не будет сохранён ни в одном дизайне.
            </NoticeStrip>
          ) : null}
          {showCopy ? (
            <SafetyCopyRow checked={copySite} onChange={setCopySite} full={copyFull}>
              {`Сначала сохранить вид с сайта как дизайн «${copyName}»`}
            </SafetyCopyRow>
          ) : null}
        </div>
        <DialogFooter className={STICKY_FOOTER}>
          <Button variant="ghost" disabled={phase !== "idle"} onClick={onClose}>
            Отмена
          </Button>
          <Button loading={phase !== "idle"} onClick={() => void submit(item)}>
            {phase === "settle" ? "Сохраняем правки…" : "Обновить дизайн"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
